import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@fantaz/shared/app-config';
import { getCurrentUser } from '@fantaz/shared/auth';
import { FzGoogleTagManagerService } from '@fantaz/shared/data-access/api';
import {
  CurrentUser,
  GAEventKeys,
  TournamentTagsEnum,
} from '@fantaz/shared/data-access/models';
import { ShareService } from '@fantaz/shared/share';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
@UntilDestroy()
@Component({
  selector: 'fz-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  @Input()
  theme?: string;

  currentYear: number = new Date().getFullYear();
  user$: Observable<CurrentUser | null | undefined> =
    new Observable<CurrentUser>();
  currentUser: CurrentUser | null | undefined;
  #dialogService = inject(MatDialog);

  unsubscribe$: Subject<void> = new Subject<void>();
  isLoggedIn: boolean;
  readonly TournamentTagsEnum = TournamentTagsEnum;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    public readonly router: Router,
    private location: Location,
    private gtmService: FzGoogleTagManagerService,
    private shareService: ShareService,
  ) {}

  ngOnInit(): void {
    this.getUser();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getActiveFragment() {
    this.route.fragment
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((fragment) => {
        if (fragment === 'promote') {
          this.onPromote(true);
        }
      });
  }

  getUser() {
    this.user$ = this.store.select(getCurrentUser).pipe(untilDestroyed(this));
    this.user$.subscribe((currentUser) => {
      this.currentUser = currentUser;
      this.isLoggedIn = !!currentUser;
    });
  }

  onPromote(hasFragment?: boolean) {
    if (hasFragment !== true) {
      const pathWithoutHash = this.location.path();
      this.router.navigateByUrl(pathWithoutHash + '#promote');
      this.router.onSameUrlNavigation = 'reload';
    }

    this.shareService.share({
      title: 'FantaZ',
      text: 'Play Casual tournaments',
      type: 'promote',
      url: environment + '/join',
      currentUser: this.currentUser,
    });
  }

  onZendeskPressed() {
    this.gtmService.pushTag({
      event: GAEventKeys.USER_REQUEST_ZENDESK,
      user_id: this.currentUser?.user.uuid,
      user_name: this.currentUser?.user.username,
      timestamp: Date.now(),
    });
  }

  onNotSellOrShareMyData(event: MouseEvent) {
    if (!this.currentUser) {
      event.preventDefault();
      this.#dialogService.open(DoNotSellOrShareMyInfoDialogComponent);
    }
  }
}

@Component({
  selector: 'fz-dont-sell-or-share-my-data-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="dialog-message">
      <h2 class="mat-headline-4" mat-dialog-title>
        Do Not Sell or Share My Personal Information
      </h2>
      <button class="dialog-close" mat-icon-button mat-dialog-close>
        <mat-icon>close</mat-icon>
      </button>
      <mat-dialog-content>
        <p class="mat-body-1">
          You might opt out by navigating to your preferences page. Please log
          in to manage your preferences.
        </p>
      </mat-dialog-content>
      <mat-dialog-actions>
        <a
          mat-flat-button
          color="primary"
          href="/auth/sign-in?redirectTo=/profile/preferences?open=dontSellMyInfo"
        >
          Log in
        </a>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      * {
        text-align: center;
      }

      .dialog-message {
        max-width: 320px;
      }

      .dialog-close {
        position: absolute;
        right: 8px;
        top: 8px;

        color: var(--text-primary, #000);
      }

      mat-dialog-content {
        p {
          color: var(--text-secondary, #4f4f4f);
        }
      }

      .mat-mdc-dialog-actions {
        margin-top: 32px;
        justify-content: center;

        a {
          width: 100%;
        }
      }
    `,
  ],
})
export class DoNotSellOrShareMyInfoDialogComponent {}
